/**
 * KLSH – Footer Reveal (Fit Content + Min Height)
 * MainContent scrolls over the footer. We reserve space equal to reveal height.
 *
 * CSS Vars (read/write):
 *  --klsh-footer-reveal-min-vh: 0..100 (set via Footer section settings)
 *  --klsh-footer-reveal-height: px reserved after #MainContent (set by this script)
 */
(() => {
  const ENABLED_CLASS = 'klsh-footer-reveal--enabled';
  const READY_CLASS = 'klsh-footer-reveal--ready';
  const HAS_BG_CLASS = 'klsh-footer-reveal--has-bg';
  const FOOTER_SELECTOR = '.klsh-footer-group-wrapper';

  const root = document.documentElement;
  const body = document.body;

  const supportsSticky = () => {
    try {
      return (
        typeof CSS !== 'undefined' &&
        (CSS.supports('position', 'sticky') || CSS.supports('position', '-webkit-sticky'))
      );
    } catch (e) {
      return false;
    }
  };

  const getWrapper = () => document.querySelector(FOOTER_SELECTOR);

  const hasRevealBackground = () => {
    const el = getWrapper();
    return !!(el && el.querySelector('.footer.klsh-footer-bg--enabled'));
  };

  // Measure *content* height (children) so we can safely apply min-height on the wrapper
  // without creating a feedback loop.
  const getContentHeight = () => {
    const el = getWrapper();
    if (!el) return 0;

    const children = Array.from(el.children || []);
    if (!children.length) {
      const r = el.getBoundingClientRect();
      return Math.max(r.height || 0, 0);
    }

    let sum = 0;
    for (const child of children) {
      const r = child.getBoundingClientRect();
      sum += (r.height || 0);
    }
    return Math.max(sum, 0);
  };

  const getMinVh = () => {
    try {
      const raw = getComputedStyle(body).getPropertyValue('--klsh-footer-reveal-min-vh').trim();
      const n = parseFloat(raw);
      return Number.isFinite(n) ? n : 0;
    } catch (e) {
      return 0;
    }
  };

  const setRevealHeight = () => {
    const contentHeight = getContentHeight();
    const hasBg = hasRevealBackground();

    body.classList.toggle(HAS_BG_CLASS, hasBg);

    const minVh = hasBg ? Math.min(Math.max(getMinVh(), 0), 100) : 0;

    const viewportHeight = window.innerHeight || 0;
    const minPx = viewportHeight > 0 ? Math.round(viewportHeight * (minVh / 100)) : 0;

    const finalHeight = Math.max(contentHeight, minPx, 0);
    root.style.setProperty('--klsh-footer-reveal-height', `${finalHeight}px`);
  };

  let raf = null;
  const scheduleRecalc = () => {
    if (raf) cancelAnimationFrame(raf);
    raf = requestAnimationFrame(() => {
      setRevealHeight();
    });
  };

  const init = () => {
    if (!body || !body.classList.contains(ENABLED_CLASS)) return;
    if (!supportsSticky()) return;

    setRevealHeight();
    body.classList.add(READY_CLASS);

    window.addEventListener('resize', scheduleRecalc, { passive: true });
    window.addEventListener('orientationchange', scheduleRecalc, { passive: true });
    window.addEventListener('load', scheduleRecalc, { passive: true });

    // Shopify Theme Editor hooks (recalc after section changes)
    document.addEventListener('shopify:section:load', scheduleRecalc);
    document.addEventListener('shopify:section:unload', scheduleRecalc);
    document.addEventListener('shopify:section:reorder', scheduleRecalc);
    document.addEventListener('shopify:section:select', scheduleRecalc);
    document.addEventListener('shopify:section:deselect', scheduleRecalc);
    document.addEventListener('shopify:block:select', scheduleRecalc);
    document.addEventListener('shopify:block:deselect', scheduleRecalc);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
